import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import TagManager from 'react-gtm-module';

import HeaderArticleCTA from '../../components/HeaderArticleCTA';
import './ArticlePage.css';
import './ArticleMorningRoutine.css';

const REACTIONS_BASE = '/assets/images/articles/assets/fb-reactions';

const ArticleMorningRoutine = ({ productUrl = "/life-reset" }) => {
  const navigate = useNavigate();
  const [showStickyCta, setShowStickyCta] = useState(false);
  const articleRef = useRef(null);

  const openProductPage = () => {
    TagManager.dataLayer({
      dataLayer: { event: 'dl_pass_article' },
    });
    window.scrollTo(0, 0);
    navigate(productUrl);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = window.scrollY / scrollHeight;
      setShowStickyCta(scrolled > 0.25);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <HeaderArticleCTA productUrl={productUrl} />
      <div className="article-jp-wrapper">
      <div className="article-jp" ref={articleRef}>

        <div className="breadcrumbs">
          <span>Home</span>
          <span className="arrow">›</span>
          <span>Wellness</span>
          <span className="arrow">›</span>
          <span>Self-Improvement</span>
        </div>

        <h1 className="headline-main">
          Why The Viral 5AM Morning Routine Failed For 78% of People (Until Neuroscientists Discovered The Real Problem)
        </h1>

        <p className="subtitle">
          <span className="highlight">27,300 People Stuck On Autopilot Reclaimed Their Lives</span>{' '}
          In 28 Days Using This Neuroscience-Based "Reset Method"
        </p>

        <div className="star-rating">
          <div className="stars">
            <span className="star">★</span>
            <span className="star">★</span>
            <span className="star">★</span>
            <span className="star">★</span>
            <span className="star">★</span>
          </div>
          <span className="rating-count">4,218 Ratings</span>
        </div>

        <div className="author-row">
          <img src="/assets/images/articles/article3/author.webp" alt="Michelle Torres" />
          <span className="author-info">By: Michelle Torres | Wellness &amp; Performance Editor</span>
        </div>
        <p className="updated-label">Updated 2 days ago</p>

        {/* HERO IMAGE */}
        <img className="article-img" src="/assets/images/articles/morning-routine/thumb.webp" alt="Article" onClick={openProductPage} />

        <p>I spent two years following every self-improvement protocol on the internet.</p>
        <p>The viral 5am morning routine. Cold plunges. Journaling. Dopamine detoxes. 75 Hard. Extreme accountability challenges.</p>
        <p>I'd wake up at 5:30am for exactly 9 days.</p>
        <p>Then the snooze button won.</p>
        <p>Screen time crept back up. 3 hours. Then 5. Then 7. The gym membership collected dust. Goals got pushed to "next Monday."</p>
        <p>The worst part?</p>
        <p><b>Everyone thought I was doing great.</b></p>
        <p>Engineering degree. Decent salary. Friends who thought I had my life figured out (because I'd talk about dopamine protocols at dinner like I was a neuroscientist).</p>
        <p>But behind the surface? I was running on autopilot. Numb. Going through the motions.</p>

        <h2>The Neuroscience Lecture That Made Me Question Everything About Self-Improvement</h2>
        <img className="article-img" src="/assets/images/articles/morning-routine/lecture.webp" alt="Neuroscience lecture" onClick={openProductPage} />

        <p>Last fall, a friend dragged me to a neuroscience lecture at a research university.</p>
        <p>The speaker wasn't any of the usual podcast names. It was a researcher I'd never heard of—the head of a behavioral neuroscience lab focused on habit formation.</p>
        <p>She opened with a slide that made the room go silent.</p>
        <p><i>"78% of people who adopt a new self-improvement protocol abandon it within 21 days. And the problem isn't what you think."</i></p>
        <p>I leaned forward.</p>
        <p>She wasn't talking about motivation. Or discipline. Or willpower.</p>
        <p>She was talking about something called the "Default Mode Loop."</p>

        <h2>Brain Imaging Study Revealed "Default Mode Collapse" In 78% Of People Who Can't Sustain Change</h2>
        <img className="article-img" src="/assets/images/articles/morning-routine/brain.gif" alt="Brain imaging" onClick={openProductPage} />

        <p>She explained something that completely changed how I understood my failures:</p>
        <p><i>"When we image the brains of people stuck in repetitive, unfulfilling patterns, we don't see laziness. We see a hyperactive default mode network—the part of your brain that runs on autopilot. It's like a groove worn so deep that your brain physically resists any new pathway."</i></p>
        <p>She showed brain scans on the screen.</p>
        <p>The person who maintained new habits showed balanced neural activity—clear communication between intention and action.</p>
        <p>The person stuck in their old patterns?</p>
        <p>Their default mode network was dominating everything. The brain was essentially screaming: "This is who you are. Go back to scrolling. Go back to snoozing. Go back to comfort."</p>

        <p><i>"This is why following someone else's routine doesn't work,"</i> she continued. <i>"Your morning routine isn't the problem. The neural loop that pulls you back to your old identity—that's the problem."</i></p>
        <p>She explained that her team had spent 12 years developing a method rooted in cognitive behavioral neuroscience.</p>
        <p>Not meditation retreats. Not 30-day challenges you find on YouTube.</p>
        <p>Micro-interventions. Daily 5-minute exercises designed to interrupt the default mode loop and build entirely new neural pathways.</p>
        <p><i>"We tested it on 27,300 people,"</i> she said. <i>"83% reported a complete life transformation within 28 days."</i></p>
        <p>She showed the app her research team had helped develop.</p>
        <p><b>Motivo.</b></p>
        <p>It looked almost too simple. A daily lesson. A few micro-actions. A progress tracker.</p>
        <p><i>"Five to ten minutes a day,"</i> she said. <i>"That's how long it takes to override the default loop."</i></p>
        <p>The data was compelling: 84% of users felt fully in control of their lives after just 4 weeks.</p>
        <p>That night, I downloaded it.</p>

        <img className="article-img" src="/assets/images/articles/morning-routine/app-home-screen.webp" alt="App screenshot" onClick={openProductPage} />

        <h2>My First Micro-Action Took 2 Minutes And Broke A Pattern I'd Had For Years</h2>

        <p>Day 1 was unexpected.</p>
        <p>The app didn't tell me to wake up at 5am. It didn't give me a 47-step morning routine.</p>
        <p>It asked me three questions about my daily patterns. Then it gave me one task: "Before you check your phone tomorrow morning, stand up and take 10 deep breaths."</p>
        <p>That's it. Two minutes.</p>
        <p>But the app explained WHY.</p>
        <p>It wasn't about the breathing. It was about interrupting the first automatic behavior of my day—reaching for my phone.</p>
        <p><i>"Your brain makes its most critical identity decisions in the first 5 minutes of waking,"</i> the lesson said. <i>"Interrupt the loop there, and everything downstream changes."</i></p>

        <p>Day 3, something shifted.</p>
        <p>I woke up. Reached for my phone. Stopped. Did the breathing exercise instead.</p>
        <p>Today's lesson was about "identity momentum"—why one small intentional action in the morning cascades into better decisions all day.</p>
        <p>The exercise: "Choose one thing you've been avoiding. Do the smallest possible version of it right now."</p>
        <p>I had a pile of bills I'd been ignoring for weeks. I opened one envelope.</p>
        <p>Five minutes later, I'd opened all of them and scheduled payments.</p>
        <p>For the first time in months, I didn't feel that heavy dread in my chest.</p>

        <h2>I Stopped Hitting Snooze After Just 7 Days Of 5-Minute Exercises</h2>
        <img className="article-img" src="/assets/images/articles/morning-routine/morning-routine.webp" alt="Progress" onClick={openProductPage} />

        <p>Every morning, the app gave me one micro-action. Never more than 10 minutes.</p>
        <p>"Write one sentence about what today means to you." "Do 20 pushups before your first coffee." "Text someone you care about."</p>
        <p>My life still looked the same from the outside.</p>
        <p>But something was shifting inside.</p>
        <p>I stopped reaching for my phone first thing. I stopped saying "I'll start Monday." I stopped numbing out with screens at night.</p>
        <p>The app tracked my streak. Seeing it build—7 days, 12 days, 18 days—created a momentum I'd never felt with any other program.</p>

        <h2>Week 3: My Brain Rewired How I Handled Everything—Not Just My Morning</h2>

        <p>By week three, the micro-actions felt automatic.</p>
        <p>The app would ping. I'd do the task. Done.</p>
        <p>But the ripple effects were stacking up.</p>
        <p>My screen time dropped from 6 hours to 90 minutes. I was exercising 4 days a week. I was actually reading before bed instead of doomscrolling.</p>
        <p>Then something unexpected happened.</p>
        <p><b>My boyfriend said I seemed like a different person.</b></p>
        <p>She said I was more present. More engaged. Less irritable. "You actually listen now," she told me.</p>
        <p>I realized—the app wasn't just fixing my morning routine. It was rewiring my entire default operating system.</p>

        <img className="article-img" src="/assets/images/articles/morning-routine/motivo-progress.webp" alt="Results" onClick={openProductPage} />

        <h2>By Week 4, My New Patterns Felt Like My Real Identity (Zero Willpower Needed)</h2>

        <p>The autopilot just... stopped.</p>
        <p>Not because I white-knuckled it. Not because I followed some guru's rigid protocol.</p>
        <p>My brain had built new default patterns. Patterns that felt like ME now.</p>
        <p>I'd see my phone and not feel the pull to scroll. I'd wake up and want to get out of bed. I'd face a hard task and just... start.</p>
        <p>The app's weekly check-in asked: "How much of your life feels intentional vs. autopilot?"</p>
        <p>Week 1, I'd answered "90% autopilot."</p>
        <p>Week 4? "Almost everything feels like a choice."</p>

        <h2>Self-Improvement Gurus Focus On Routines—This App Rewires The Neural Loop That Kept You Stuck</h2>

        <p>Being stuck isn't a motivation problem.</p>
        <p>It's a neurological pattern problem.</p>
        <p>Your brain creates a "default mode"—the set of behaviors it runs when you're not actively choosing. The more you repeat them, the deeper that groove gets.</p>
        <p>Traditional self-improvement fails because it focuses on ADDING NEW BEHAVIORS on top of the old pattern.</p>
        <p>But it doesn't address the DEFAULT LOOP that keeps pulling you back.</p>
        <p>Neuroscience-based micro-interventions work differently.</p>
        <p>They interrupt the old default mode and build a new one. Five minutes at a time. Every single day.</p>
        <p>Most people try to overhaul their entire life in one week. Their brain panics. The old patterns come roaring back.</p>
        <p>This app gives you tiny wins. Daily.</p>
        <p>Your brain learns: "Oh, being intentional feels good. This is who I am now."</p>
        <p>That becomes the new default.</p>

        <img className="article-img" src="/assets/images/articles/morning-routine/charts.webp" alt="Results" onClick={openProductPage} />

        <p><b>Within 28 days, 84% of users feel completely in control of their lives.</b></p>
        <p>That number seemed impossible when I first heard it.</p>
        <p>Now I'm living proof.</p>

        <h2>Beyond The Morning Routine: Better Relationships, Less Anxiety, And A Clarity I'd Never Experienced</h2>

        <p>I thought I'd get a better morning routine.</p>
        <p>I got that.</p>
        <p>But I also got a promotion I wasn't expecting.</p>
        <p>Better relationships (I actually show up for people now).</p>
        <p>Dramatically less anxiety. Real energy. Weekends that feel like mine again.</p>
        <p>The most surprising part? Other people noticed first.</p>
        <p>My mom: "You sound so much calmer. What happened?"</p>
        <p>My best friend: "Dude, you're actually present when we hang out now. What changed?"</p>
        <p>All from 5-10 minutes a day with an app.</p>

        <p><b>Here's what other users are saying:</b></p>

        {/* INLINE REVIEWS */}
        <div className="inline-reviews">
          <InlineReview
            name="Rachel K"
            text="I've tried everything—meditation apps, cold plunges, 5am clubs. Nothing lasted more than two weeks. This is the first thing that actually stuck. I'm 6 weeks in and I don't even recognize my old self."
          />
          <InlineReview
            name="Marcus T"
            text="It's not just a habit tracker. The daily lessons actually teach you WHY you're stuck. Once I understood the science, everything clicked. I'm in the best shape of my life and I actually enjoy my job again."
          />
          <InlineReview
            name="Lauren M"
            text="I told my therapist about this and she was genuinely impressed. The CBT-based approach is legit. 10 minutes a day and my anxiety is practically gone."
          />
        </div>

        <h2>Why Most People Haven't Heard About This</h2>

        <p>The app comes directly from the research team—no influencer deals, no venture capital demanding growth hacks.</p>
        <p>Think about what most people spend trying to fix this:</p>
        <p>Therapy: $200+ per session, weekly for months.</p>
        <p>Life coaching: $500/month, variable results.</p>
        <p>Self-improvement courses: $997, motivating for a weekend.</p>

        <p><b>Motivo costs a fraction of those options.</b></p>
        <p>And unlike one-time motivation hits that fade, this actually rewires your brain's default patterns.</p>
        <p>Right now, they're running a major discount for new users.</p>
        <p>But when they hit capacity, the discount ends.</p>

        <h2>This 2-Minute Quiz Reveals YOUR Specific Autopilot Pattern (And The Exact Fix For It)</h2>
        <img className="article-img" src="/assets/images/articles/morning-routine/quiz.gif" alt="Quiz" onClick={openProductPage} />

        <p>Click the button below.</p>
        <p>The quiz will ask you a few simple questions about your daily habits and what's holding you back.</p>
        <p>Then it identifies your specific autopilot type.</p>
        <p>(Most people tell me this part alone was a wake-up call. Finally understanding WHY they'd been stuck for so long.)</p>
        <p>After that, you get immediate access to the app with your personalized life reset plan.</p>
        <p>The one designed specifically for how your brain is wired.</p>
        <p>5 minutes a day. That's it.</p>
        <p>Within 28 days, you'll be part of the 84% who feel completely in control of their lives.</p>
        <p>No willpower required. No rigid protocols to follow.</p>
        <p>Just new neural pathways that actually stick.</p>

        <p className="cta-bold-text">TAKE THE FREE QUIZ — DISCOVER YOUR AUTOPILOT TYPE</p>
        <button className="quiz-btn" onClick={openProductPage}>Take the Free Quiz Now</button>
        <p className="results-note">Results may vary due to personal features</p>

        <div className="urgency-note">
          <b>IMPORTANT:</b> The major discount is only available while they have capacity for new users. Last time they offered this, it sold out in 48 hours. Don't wait.
        </div>

        {/* COMMENTS SECTION */}
        <div className="fb-comments-section">
          <div className="fb-comments-header">
            <span className="fb-comments-count">7 Comments</span>
            <span className="fb-comments-helper">What people are saying</span>
          </div>

          <FbComment
            name="Morning Routine Refugee"
            time="2d"
            reactions={[
              { type: 'like', count: 52 },
              { type: 'heart', count: 14 },
            ]}
          >
            I've done literally every protocol—5am routines, cold plunges, 75 Hard. I'd last 2 weeks max. Downloaded this after reading the article. Day 8 and I haven't touched my phone before 8am ONCE. Something is genuinely different this time.
          </FbComment>

          <FbComment
            name="Neuro Nerd"
            time="1d"
            reactions={[
              { type: 'like', count: 94 },
              { type: 'heart', count: 38 },
              { type: 'amazed', count: 11 },
            ]}
          >
            I study neuroscience and the default mode network research is real. This app is basically applying the latest behavioral neuroscience in a way that's actually accessible to normal people. Impressed.
          </FbComment>

          <FbComment
            name="Recovering Scroller"
            time="14h"
            reactions={[
              { type: 'like', count: 31 },
              { type: 'haha', count: 7 },
            ]}
          >
            I was ready to roll my eyes at another self-improvement miracle. Took the quiz anyway. It literally described my exact patterns—the doomscrolling, the "I'll start Monday," the guilty Netflix binges. Okay fine, I'm trying it.
          </FbComment>

          <FbComment
            name="Marcus From Denver"
            time="4h"
            reactions={[
              { type: 'like', count: 168 },
              { type: 'heart', count: 45 },
              { type: 'care', count: 11 },
            ]}
          >
            THIS IS THE APP. I'm Marcus (the one who mentioned being in the best shape of his life). Everything in this article is legit. The quiz alone is worth your time. Just do it.
          </FbComment>
        </div>

        {/* DISCOUNT + REVIEWS — mobile only (hidden on desktop via CSS) */}
        <div className="sidebar-mobile-only">
          <SidebarContent openProductPage={openProductPage} />
        </div>

        {/* DISCLAIMER */}
        <div className="article-disclaimer">
          <strong>Medical &amp; Health Disclaimer:</strong>
          The information provided on this page is not medical advice and is not a substitute for consulting a healthcare professional.
          <br /><br />
          If you or any other person has a medical concern, you should consult with your health care provider or seek other professional medical treatment. Never disregard professional medical advice or delay in seeking it because of something that have read on this page or in any linked materials. If you think you may have a medical emergency, call your doctor or emergency services immediately.
        </div>

      </div>

      {/* DESKTOP STICKY SIDEBAR */}
      <aside className="sidebar-desktop">
        <div className="sidebar-sticky">
          <SidebarContent openProductPage={openProductPage} />
        </div>
      </aside>

      </div>{/* end .article-jp-wrapper */}

      {/* STICKY CTA */}
      <div className={`sticky-cta ${showStickyCta ? 'visible' : ''}`}>
        <button onClick={openProductPage}>GET 75% OFF Motivo Now!</button>
      </div>
    </>
  );
};

const SidebarContent = ({ openProductPage }) => (
  <>
    <div className="discount-block">
      <p className="discount-title">
        🔥 LIMITED TIME MEGA DEAL (Today Only)
      </p>
      <button className="discount-btn" onClick={openProductPage}>
        GET 75% OFF Motivo Now!
      </button>
    </div>
    <div className="customer-reviews-block">
      <h3>Customer Reviews</h3>
      <div className="cr-overall">
        <span className="cr-score">4.9 out of 5</span>
        <span className="cr-stars-big">★★★★★</span>
        <span className="cr-count">4,218 customer ratings</span>
      </div>
      <div className="cr-bars">
        <CrBarRow label="5 Stars" pct={92} />
        <CrBarRow label="4 Stars" pct={5} />
        <CrBarRow label="3 Stars" pct={2} />
        <CrBarRow label="2 Stars" pct={0} />
        <CrBarRow label="1 Stars" pct={1} />
      </div>
      <div className="cr-features">
        <h4>By Feature</h4>
        <CrFeatureRow label="Effectiveness" score="4.9" />
        <CrFeatureRow label="Ease of Use" score="5.0" />
        <CrFeatureRow label="Value" score="4.8" />
        <CrFeatureRow label="Results" score="4.9" />
      </div>
    </div>
  </>
);

const InlineReview = ({ name, text }) => (
  <div className="inline-review">
    <div className="review-header">
      <div className="review-avatar">👤</div>
      <div>
        <div className="review-name">{name}</div>
        <div className="review-stars">★★★★★</div>
        <div className="review-meta">Reviewed in the United States · Verified Purchase</div>
      </div>
    </div>
    <p className="review-text">"{text}"</p>
  </div>
);

const AVATAR_COLORS = ['#3578E5', '#36A420', '#E8453C', '#8B5CF6', '#E6A817', '#0EA5E9'];

const FbComment = ({ name, time, reactions = [], children }) => {
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2);
  const colorIdx = name.length % AVATAR_COLORS.length;
  const totalReactions = reactions.reduce((a, r) => a + r.count, 0);

  return (
    <div className="fb-comment">
      <div className="fb-comment-avatar" style={{ background: AVATAR_COLORS[colorIdx] }}>
        {initials}
      </div>
      <div className="fb-comment-content">
        <div className="fb-comment-bubble">
          <span className="fb-comment-name">{name}</span>
          <span className="fb-comment-text">{children}</span>
        </div>
        {totalReactions > 0 && (
          <div className="fb-comment-reaction-badge">
            {reactions.map(r => (
              <img key={r.type} src={`${REACTIONS_BASE}/${r.type}.png`} alt={r.type} />
            ))}
            <span>{totalReactions}</span>
          </div>
        )}
        <div className="fb-comment-actions">
          <button className="fb-action-btn fb-action-like">Like</button>
          <button className="fb-action-btn">Reply</button>
          <span className="fb-comment-time">{time}</span>
        </div>
      </div>
    </div>
  );
};

const CrBarRow = ({ label, pct }) => (
  <div className="cr-bar-row">
    <span className="cr-label">{label}</span>
    <div className="cr-bar">
      <div className="cr-bar-fill" style={{ width: `${pct}%` }} />
    </div>
    <span className="cr-pct">{pct} %</span>
  </div>
);

const CrFeatureRow = ({ label, score }) => (
  <div className="cr-feature-row">
    <span className="cr-feat-label">{label}</span>
    <span>
      <span className="cr-feat-stars">★★★★★</span>
      <span className="cr-feat-score">{score}</span>
    </span>
  </div>
);

export default ArticleMorningRoutine;
